/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */
import React from 'react';
import { ChartLineIcon, TableIcon } from 'lucide-react';

import { Button, Dropdown } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import { Bloc } from '@mairistem/administres-utils';

import useEntiteStore from '@/zustand/useEntiteStore';
import type { Individu } from '@/types/Individu';
import { getDoyen } from '@/routes/individus';

import VueStatistiqueGraphique from './VueStatistiqueGraphique';
import VueStatistiqueTableau from './VueStatistiqueTableau';

interface BlocStatistiquesProps {
  setLoadingTab: (loading: boolean) => void;
  setLoadingGraph: (loading: boolean) => void;
  style?: React.CSSProperties;
}

const currentYear = new Date().getFullYear();

const BlocStatistiques = (props: BlocStatistiquesProps) => {
  const { setLoadingTab, setLoadingGraph, style } = props;

  const [modeTableau, setModeTableau] = React.useState(true);
  const [selectedYearMin, setSelectedYearMin] = React.useState(currentYear - 5);
  const [selectedYearMax, setSelectedYearMax] = React.useState(currentYear);
  const [doyen, setDoyen] = React.useState<Individu>();

  const blocked = useEntiteStore((state) => state.blocked);

  React.useEffect(() => {
    if (!blocked) {
      request.get(getDoyen()).then((response: Individu) => {
        setDoyen(response);
      });
    }
  }, [blocked]);

  React.useEffect(() => {
    if (selectedYearMin > selectedYearMax) {
      setSelectedYearMin(selectedYearMax);
    }
  }, [selectedYearMin, selectedYearMax]);

  const yearOptions = React.useMemo(() => {
    const doyenYear = doyen
      ? new Date(doyen.dateRecensement).getFullYear()
      : currentYear;
    const startYear = Number.isNaN(doyenYear) ? currentYear : doyenYear;
    const years = [];

    for (let i = currentYear; i >= startYear; i -= 1) {
      years.push({ key: i, text: String(i), value: i });
    }

    return years;
  }, [doyen]);

  React.useEffect(() => {
    if (!yearOptions.length) {
      return;
    }

    const minAvailableYear = yearOptions[yearOptions.length - 1].value;
    const maxAvailableYear = yearOptions[0].value;

    setSelectedYearMin(Math.max(minAvailableYear, currentYear - 5));
    setSelectedYearMax((prev) =>
      Math.min(Math.max(prev, minAvailableYear), maxAvailableYear),
    );
  }, [yearOptions]);

  const yearOptionsMin = React.useMemo(
    () => yearOptions.filter((option) => option.value <= selectedYearMax),
    [yearOptions, selectedYearMax],
  );

  const yearOptionsMax = React.useMemo(
    () => yearOptions.filter((option) => option.value >= selectedYearMin),
    [yearOptions, selectedYearMin],
  );

  const styleHeader = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5em',
    justifyContent: 'space-between',
    width: '100%',
    height: '2em',
  };

  if (modeTableau) {
    return (
      <Bloc
        icon="chart bar"
        header={
          <div style={styleHeader}>
            <div>
              <span>Statistiques sur l&apos;année </span>
              <Dropdown
                inline
                scrolling
                options={yearOptions}
                value={selectedYearMax}
                onChange={(_, data) => setSelectedYearMax(data.value as number)}
                style={{ fontWeight: 600 }}
              />
            </div>
            <Button
              icon={<ChartLineIcon size="1em" />}
              onClick={() => setModeTableau(false)}
              compact
            />
          </div>
        }
        style={style}
      >
        <VueStatistiqueTableau
          annee={selectedYearMax}
          setLoading={setLoadingTab}
        />
      </Bloc>
    );
  }

  return (
    <Bloc
      icon="chart bar"
      header={
        <div style={styleHeader}>
          <div>
            <span>Statistiques entre les années </span>
            <Dropdown
              inline
              scrolling
              options={yearOptionsMin}
              value={selectedYearMin}
              onChange={(_, data) => setSelectedYearMin(data.value as number)}
              style={{ fontWeight: 600 }}
            />
            <span>et </span>
            <Dropdown
              inline
              scrolling
              options={yearOptionsMax}
              value={selectedYearMax}
              onChange={(_, data) => setSelectedYearMax(data.value as number)}
              style={{ fontWeight: 600 }}
            />
          </div>
          <Button
            icon={<TableIcon size="1em" />}
            onClick={() => setModeTableau(true)}
            compact
          />
        </div>
      }
      style={{ display: 'flex', flex: 1 }}
    >
      <VueStatistiqueGraphique
        anneeMin={selectedYearMin}
        anneeMax={selectedYearMax}
        setLoading={setLoadingGraph}
      />
    </Bloc>
  );
};

export default BlocStatistiques;
